import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  signInWithCredential,
  PhoneAuthProvider,
  RecaptchaVerifier,
  ConfirmationResult,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { auth, db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";

function translatePhoneAuthError(message: string): string {
  if (!message) return "حدث خطأ أثناء إرسال الرمز";
  const lower = message.toLowerCase();
  if (
    lower.includes("blocked") ||
    lower.includes("unusual activity") ||
    lower.includes("too many")
  ) {
    return "تم حظر هذا الجهاز مؤقتاً بسبب كثرة المحاولات. انتظر ساعة وحاول مرة أخرى أو استخدم تسجيل الدخول بالبريد.";
  }
  if (lower.includes("billing_not_enabled")) {
    return "يجب تفعيل الفوترة في مشروع Firebase لاستخدام تسجيل الدخول بالهاتف. اذهب إلى Firebase Console → Project settings → إضافة بطاقة دفع (Blaze plan).";
  }
  if (lower.includes("internal-error")) {
    return "تعذّر إرسال رمز التحقق (خطأ داخلي). غالباً السبب: لم يُفعَّل إرسال SMS (خطة Blaze)، أو قيود على مفتاح API في Google Cloud، أو النطاق غير مضبوط. راجع إعدادات الهاتف في Firebase وGoogle Cloud.";
  }
  return message;
}

export interface UserAuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  signUpWithEmail: (email: string, password: string) => Promise<boolean>;
  signInWithEmail: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  signInWithPhone: (phoneNumber: string) => Promise<boolean>;
  confirmPhoneCode: (code: string) => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  recaptchaContainerId: string;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

const RECAPTCHA_CONTAINER_ID = "recaptcha-container";
const AUTH_INIT_FAILSAFE_MS = 10000;

export function UserAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const nativeVerificationIdRef = useRef<string | null>(null);
  const phoneAuthListenersRef = useRef<Array<{ remove: () => Promise<void> }>>(
    []
  );

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsLoading((prev) => {
        if (!prev) return prev;
        console.warn(
          "[UserAuth] onAuthStateChanged timeout, unlocking UI with signed-out state"
        );
        return false;
      });
    }, AUTH_INIT_FAILSAFE_MS);

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      window.clearTimeout(timeoutId);
      setUser(firebaseUser);
      setIsLoading(false);
    });

    return () => {
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const signUpWithEmail = async (
    email: string,
    password: string
  ): Promise<boolean> => {
    setError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/email-already-in-use") {
        setError("البريد الإلكتروني مستخدم بالفعل");
      } else if (code === "auth/weak-password") {
        setError("كلمة المرور ضعيفة (6 أحرف على الأقل)");
      } else if (code === "auth/invalid-email") {
        setError("البريد الإلكتروني غير صحيح");
      } else {
        setError("حدث خطأ أثناء إنشاء الحساب");
      }
      return false;
    }
  };

  const signInWithEmail = async (
    email: string,
    password: string
  ): Promise<boolean> => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/user-not-found") {
        setError("هذا الحساب غير موجود قم بإنشاء حساب");
      } else if (
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential"
      ) {
        setError("البريد الإلكتروني أو كلمة المرور غير صحيحة");
      } else if (code === "auth/too-many-requests") {
        setError("تم تجاوز عدد المحاولات، حاول لاحقاً");
      } else {
        setError("حدث خطأ أثناء تسجيل الدخول");
      }
      return false;
    }
  };

  const signOutUser = async () => {
    setError(null);
    confirmationResultRef.current = null;
    nativeVerificationIdRef.current = null;
    phoneAuthListenersRef.current.forEach((l) => l.remove().catch(() => {}));
    phoneAuthListenersRef.current = [];
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear?.();
      } catch {
        // ignore
      }
      recaptchaVerifierRef.current = null;
    }
    await signOut(auth);
  };

  const getOrCreateRecaptchaVerifier = (): RecaptchaVerifier => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (!container) {
      throw new Error("Recaptcha container not found");
    }
    const verifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        recaptchaVerifierRef.current = null;
      },
    });
    recaptchaVerifierRef.current = verifier;
    verifier.render().catch((err) => {
      console.warn("RecaptchaVerifier render:", err);
    });
    return verifier;
  };

  const signInWithPhone = async (phoneNumber: string): Promise<boolean> => {
    setError(null);
    nativeVerificationIdRef.current = null;

    if (!Capacitor.isNativePlatform() && typeof window !== "undefined") {
      const isLocalhost =
        window.location?.hostname === "localhost" ||
        window.location?.hostname === "127.0.0.1";
      if (isLocalhost) {
        setError(
          "تسجيل الدخول بالهاتف لا يعمل على localhost. استخدم رقم اختبار من Firebase Console أو نفّذ التطبيق على نطاق حقيقي."
        );
        return false;
      }
    }

    if (Capacitor.isNativePlatform()) {
      return new Promise<boolean>((resolve) => {
        const cleanup = () => {
          phoneAuthListenersRef.current.forEach((l) =>
            l.remove().catch(() => {})
          );
          phoneAuthListenersRef.current = [];
        };

        const onCodeSent = (event: { verificationId: string }) => {
          if (!event?.verificationId?.trim()) {
            console.error("[PhoneAuth] phoneCodeSent with empty verificationId");
            setError(
              "لم يتم إرسال الرمز. جرّب إضافة رقم اختبار من Firebase Console أو التثبيت من Play Store."
            );
            cleanup();
            resolve(false);
            return;
          }
          nativeVerificationIdRef.current = event.verificationId;
          cleanup();
          resolve(true);
        };

        const onVerificationFailed = (event: { message?: string }) => {
          console.error("[PhoneAuth] verification failed:", event);
          const raw = event?.message || "";
          setError(translatePhoneAuthError(raw));
          cleanup();
          resolve(false);
        };

        (async () => {
          try {
            const { FirebaseAuthentication } = await import(
              "@capacitor-firebase/authentication"
            );
            if (import.meta.env.DEV) {
              console.info("[PhoneAuth] native", {
                platform: Capacitor.getPlatform(),
                isNative: Capacitor.isNativePlatform(),
                pluginAvailable: Capacitor.isPluginAvailable(
                  "FirebaseAuthentication"
                ),
              });
            }
            const codeSentHandle = await FirebaseAuthentication.addListener(
              "phoneCodeSent",
              onCodeSent
            );
            const failedHandle = await FirebaseAuthentication.addListener(
              "phoneVerificationFailed",
              onVerificationFailed
            );
            phoneAuthListenersRef.current = [codeSentHandle, failedHandle];
            await FirebaseAuthentication.signInWithPhoneNumber({
              phoneNumber,
              timeout: 0,
            });
          } catch (err: unknown) {
            console.error("[PhoneAuth] signInWithPhoneNumber error:", err);
            const code = (err as { code?: string })?.code;
            const message = (err as { message?: string })?.message;
            if (
              code === "UNIMPLEMENTED" ||
              (typeof message === "string" &&
                message.toLowerCase().includes("not implemented on"))
            ) {
              setError(
                "تعذّر تفعيل التحقق من الهاتف. أغلق التطبيق، ثم أعد التثبيت بعد بناء نسخة جديدة: npm run build ثم npx cap sync android."
              );
            } else {
              setError(
                code === "auth/invalid-phone-number"
                  ? "رقم الهاتف غير صحيح"
                  : code === "auth/operation-not-allowed"
                    ? "تسجيل الدخول بالهاتف غير مفعّل. تحقق من إعدادات Firebase وSHA-1."
                    : code === "auth/quota-exceeded"
                      ? "تم تجاوز الحد المسموح. استخدم رقم اختبار أو حاول لاحقاً."
                      : translatePhoneAuthError(message || "")
              );
            }
            cleanup();
            resolve(false);
          }
        })();
      });
    }

    try {
      const verifier = getOrCreateRecaptchaVerifier();
      const result = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      confirmationResultRef.current = result;
      return true;
    } catch (err: unknown) {
      console.error("[PhoneAuth] web signInWithPhoneNumber error:", err);
      const code = (err as { code?: string })?.code;
      const message = (err as { message?: string })?.message;
      if (code === "auth/invalid-phone-number") {
        setError("رقم الهاتف غير صحيح");
      } else if (code === "auth/too-many-requests") {
        setError("تم تجاوز عدد المحاولات، حاول لاحقاً");
      } else if (
        code === "auth/captcha-check-failed" ||
        code === "auth/missing-client-identifier"
      ) {
        setError(
          "فشل التحقق. جرّب استخدام تسجيل الدخول بالبريد الإلكتروني أو تحقق من اتصال الإنترنت."
        );
      } else if (code === "auth/operation-not-allowed") {
        setError("تسجيل الدخول بالهاتف غير مفعّل. تواصل مع الدعم.");
      } else if (code === "auth/quota-exceeded") {
        setError(
          "تم تجاوز الحد المسموح. استخدم رقم اختبار من Firebase Console أو حاول لاحقاً."
        );
      } else if (code === "auth/argument-error") {
        setError("خطأ في التحقق. تأكد أن النطاق مضاف في Authorized domains.");
      } else if (code === "auth/internal-error") {
        setError(
          "تعذّر إرسال رمز التحقق. تحقق من: تفعيل خطة Blaze لإرسال SMS، وإضافة couponsapp.netlify.app في Authorized domains (بدون https)، وعدم تقييد مفتاح المتصفح في Google Cloud بما يمنع موقعك، وتفعيل Identity Toolkit API."
        );
      } else {
        setError(translatePhoneAuthError(message || ""));
      }
      return false;
    }
  };

  const checkBlockedPhone = async (phone: string | null): Promise<boolean> => {
    if (!phone?.trim()) return false;
    const phoneDigits = phone.replace(/\D/g, "");
    if (!phoneDigits) return false;
    const blockedRef = doc(db, "blockedPhones", phoneDigits);
    const snap = await getDoc(blockedRef);
    return snap.exists();
  };

  const confirmPhoneCode = async (code: string): Promise<boolean> => {
    setError(null);

    const verificationId = nativeVerificationIdRef.current;
    if (verificationId) {
      try {
        const credential = PhoneAuthProvider.credential(verificationId, code);
        const { user: signedInUser } = await signInWithCredential(
          auth,
          credential
        );
        nativeVerificationIdRef.current = null;
        phoneAuthListenersRef.current.forEach((l) => l.remove().catch(() => {}));
        phoneAuthListenersRef.current = [];
        const isBlocked = await checkBlockedPhone(
          signedInUser?.phoneNumber ?? null
        );
        if (isBlocked) {
          await signOut(auth);
          setError(
            "هذا الحساب تم حذفه ولا يمكن إعادة التسجيل بهذا الرقم."
          );
          return false;
        }
        return true;
      } catch (err: unknown) {
        const codeErr = (err as { code?: string })?.code;
        setError(
          codeErr === "auth/invalid-verification-code"
            ? "رمز التحقق غير صحيح"
            : "فشل التحقق من الرمز"
        );
        return false;
      }
    }

    const result = confirmationResultRef.current;
    if (!result) {
      setError("لم يتم إرسال رمز التحقق بعد");
      return false;
    }
    try {
      const { user: signedInUser } = await result.confirm(code);
      confirmationResultRef.current = null;
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear?.();
        } catch {
          // ignore
        }
        recaptchaVerifierRef.current = null;
      }
      const isBlocked = await checkBlockedPhone(
        signedInUser?.phoneNumber ?? null
      );
      if (isBlocked) {
        await signOut(auth);
        setError("هذا الحساب تم حذفه ولا يمكن إعادة التسجيل بهذا الرقم.");
        return false;
      }
      return true;
    } catch (err: unknown) {
      const codeErr = (err as { code?: string })?.code;
      if (codeErr === "auth/invalid-verification-code") {
        setError("رمز التحقق غير صحيح");
      } else {
        setError("فشل التحقق من الرمز");
      }
      return false;
    }
  };

  const sendPasswordReset = async (email: string): Promise<boolean> => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/user-not-found") {
        setError("لا يوجد حساب بهذا البريد");
      } else {
        setError("حدث خطأ أثناء إرسال رابط إعادة التعيين");
      }
      return false;
    }
  };

  return (
    <UserAuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        clearError,
        signUpWithEmail,
        signInWithEmail,
        signOut: signOutUser,
        signInWithPhone,
        confirmPhoneCode,
        sendPasswordReset,
        recaptchaContainerId: RECAPTCHA_CONTAINER_ID,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error("useUserAuth must be used within UserAuthProvider");
  }
  return context;
}
